import type {
  BenchmarkWorkerPayload,
  BenchmarkWorkerResult,
  GpuBenchmarkResult,
} from '../types'

export const BENCHMARK_WINDOW_MS = 500
export const WORKER_TIMEOUT_MS = 7_000
export const BENCHMARK_CANVAS_SIZE = 256

function createWorker(): Worker | null {
  try {
    return new Worker(new URL('./perf-probe.worker.ts', import.meta.url), {
      type: 'module',
    })
  } catch {
    return null
  }
}

export function runGpuBenchmarkInWorker(): Promise<GpuBenchmarkResult> {
  return new Promise((resolve) => {
    if (typeof OffscreenCanvas === 'undefined' || typeof Worker === 'undefined') {
      resolve({ frames: 0, elapsedMs: 0, error: 'worker-unavailable' })
      return
    }

    let worker: Worker | null = createWorker()
    let settled = false
    const finish = (result: GpuBenchmarkResult): void => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      worker?.terminate()
      worker = null
      resolve(result)
    }
    const timer = setTimeout(() => {
      finish({ frames: 0, elapsedMs: WORKER_TIMEOUT_MS, error: 'worker-timeout' })
    }, WORKER_TIMEOUT_MS)

    if (!worker) {
      finish({ frames: 0, elapsedMs: 0, error: 'worker-unavailable' })
      return
    }

    worker.onmessage = (event: MessageEvent<BenchmarkWorkerResult>) => {
      const data = event.data
      if (data.error) {
        finish({ frames: 0, elapsedMs: data.elapsedMs, error: 'worker-unavailable' })
        return
      }
      finish({ frames: data.frames, elapsedMs: data.elapsedMs })
    }
    worker.onerror = () => {
      finish({ frames: 0, elapsedMs: 0, error: 'worker-unavailable' })
    }

    try {
      const canvas = new OffscreenCanvas(BENCHMARK_CANVAS_SIZE, BENCHMARK_CANVAS_SIZE)
      worker.postMessage(
        { canvas, durationMs: BENCHMARK_WINDOW_MS } satisfies BenchmarkWorkerPayload,
        [canvas],
      )
    } catch {
      finish({ frames: 0, elapsedMs: 0, error: 'worker-unavailable' })
    }
  })
}
